import type { Platform } from "@adora/shared/types/ad-platforms";
import type { AspectRatio } from "@adora/shared/types/aspect-ratios";
import type { PlacementSelector } from "@adora/shared";
import DataDogManager from "@adora/shared/utils/datadog";

export interface ImageDownloadTrackingContext {
  feature:
    | "lightbox"
    | "creative_review"
    | "creative_review_selection"
    | "projects"
    | "ad_asset_group"
    | "ad_builder"
    | "publishing_ads"
    | "internal_ad_asset_group"
    | "internal_ad_builder_images";
  download_type: "single" | "bulk";
  asset_count?: number;
  image_id?: number;
  theme_id?: number;
  ad_asset_group_id?: number;
  ad_submission_id?: number;
  ad_asset_image_id?: number;
  ad_builder_image_id?: number;
  aspect_ratio?: AspectRatio;
  templated?: boolean;
  resized?: boolean;
  platform?: Platform;
  placement_platform?: string;
  placement_type?: string;
  brand_id?: number;
}

/** Tracking fields for the selection a download addressed (aspect ratio or placement). */
export function selectorTrackingFields(
  selector: PlacementSelector
): Pick<ImageDownloadTrackingContext, "aspect_ratio" | "placement_platform" | "placement_type"> {
  return selector.kind === "placement"
    ? { placement_platform: selector.platform, placement_type: selector.placementType }
    : { aspect_ratio: selector.aspectRatio };
}

interface ImageDownloadResult {
  file_name?: string;
  file_size_bytes?: number;
  duration_ms: number;
}

interface ImageDownloadFailure {
  error_message?: string;
  duration_ms: number;
}

export function trackImageDownloadCompleted(context: ImageDownloadTrackingContext, result: ImageDownloadResult) {
  DataDogManager.reportCustomAction("image_download_completed", {
    ...context,
    ...result
  });
}

export function trackImageDownloadFailed(context: ImageDownloadTrackingContext, error: ImageDownloadFailure) {
  DataDogManager.reportCustomAction("image_download_failed", {
    ...context,
    ...error
  });
}

export function createDownloadTrackingCallbacks(context: ImageDownloadTrackingContext) {
  return {
    onCompleteTracking: (result: { file_name: string; file_size_bytes: number; duration_ms: number }) =>
      trackImageDownloadCompleted(context, result),
    onFailedTracking: (error: { error_message: string; duration_ms: number }) =>
      trackImageDownloadFailed(context, error)
  };
}
